/**
 * Created by karthikeyan.k on 06-02-2019.
 */

import { Injectable, inject } from '@angular/core';
import {
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { finalize, tap } from 'rxjs/operators';
import { ErrorPopupService } from '../services/error.popup.service';
import { UserService } from '../services/user.service';
import { UtilService } from '../util/UtilService';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  public errorPopupService = inject(ErrorPopupService);
  public userService = inject(UserService);
  public utilService = inject(UtilService);

  constructor() {
    this.errorPopupService.testMethod();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    let status = '';
    let statuscode = '';
    const rngDomain = this.utilService.rngServerURL;

    const requestUrlDoesNotContain = (str) => {
      return !('' + request.url).includes(str);
    };

    if (requestUrlDoesNotContain('DetailedGameHistory')) {
      if (
        this.userService.user.sessiontoken != undefined &&
        this.userService.user.sessiontoken != ''
      ) {
        logStaleActivityTimestamp(); // Logs the stale activity epoch timestamp if older than 5 minutes

        request = request.clone({
          setHeaders: {
            sessiontoken: this.userService.user.sessiontoken,
            ...(isSamePageOrRng(request.url, rngDomain)
              ? getAuthHeaders()
              : {}),
          },
        });
      }
    }
    // requirement for session end popup
    /*
SESSION_SLEEP_TIME 15 min
SESSION_IDLE_TIME 60 min
SESSION_BROW_CLOSE_TIME 15 min
 */
    return next.handle(request).pipe(
      tap(
        (event) => {
          if (event instanceof HttpResponse) {
            if (event.headers.get('x-fcg-access-token')) {
              if (
                shouldUpdateSessionToken(
                  event.headers.get('x-fcg-access-token')
                )
              ) {
                console.log(
                  'interceptor updating session token with value: ',
                  event.headers.get('x-fcg-access-token')
                );
              }
              this.userService.user.sessiontoken =
                event.headers.get('x-fcg-access-token');
            }
            const responseUrl = event.url || '';
            if (isSamePageOrRng(responseUrl, rngDomain)) {
              storeAuthHeadersInSessionStorage(event.headers);
            }
          }
          status = 'success';
        },
        (error) => {
          status = error.status;
        }
      ),
      finalize(() => {
        if (status !== 'success') {
          if (
            !no_generic_error_routes.some((err_route) =>
              request.url.includes(err_route)
            )
          ) {
            this.errorPopupService.showPopup({
              httpCode: status as unknown as number,
              additionalErrorCode:
                status && status?.toString() !== '401'
                  ? `0x${status?.toString()}`
                  : '',
            });
          }
        }
      })
      /*map((event: HttpEvent<any>) => {
         if (event instanceof HttpResponse) {
         //console.log('event--->>>', event);
         }
         return event;
         }),
         catchError(error => {
         // if (error instanceof HttpErrorResponse) {

         // }
         throwError(error);

         })*/
    );
  }
}

function shouldUpdateSessionToken(accessToken: string): boolean {
  const currentAccessToken = sessionStorage.getItem('x-fcg-access-token');
  return currentAccessToken !== accessToken;
}

// New helper function to determine if a URL is a valid domain or a valid path.
// Returns the domain string if httpUrl is a full URL.
// Returns true if httpUrl is not a full URL but is a valid path.
// Returns null if httpUrl is neither a valid full URL nor a valid path.
function getDomainOrValidatePath(httpUrl: string): string | true | null {
  let urlParseErrorForLogging: Error | null = null;
  try {
    // Attempt to parse as a full URL and get hostname
    return new URL(httpUrl).hostname;
  } catch (e: unknown) {
    urlParseErrorForLogging = e instanceof Error ? e : new Error(String(e));
    // If URL parsing fails, proceed to validate as a path
  }

  try {
    // isValidUrlPath will return true if valid, or throw an error if invalid.
    // The type guard `path is string` in isValidUrlPath's signature means that
    // if it returns, `path` is confirmed as a string, and in this context, it returns `true`.
    if (isValidUrlPath(httpUrl)) {
      return true; // Indicates it's a valid path (but not a full URL that was parsable initially)
    }
    // The following lines are effectively defensive coding if isValidUrlPath's contract changes
    // to return false instead of always throwing on failure. Given current isValidUrlPath,
    // an error would be caught by the catch block below if validation fails.
    // If isValidUrlPath could return false:
    // console.error(
    //   `Comprehensive error for '${httpUrl}':\n` +
    //   `1. URL parsing failed: ${urlParseErrorForLogging?.message || 'Unknown URL parse error'}\n` +
    //   `2. Path validation returned false.`
    // );
    // return null;
  } catch (pathValidationError: unknown) {
    const pathValidationErrorInstance =
      pathValidationError instanceof Error
        ? pathValidationError
        : new Error(String(pathValidationError));
    console.error(
      `Comprehensive error for '${httpUrl}':\n` +
        `1. URL parsing failed: ${
          urlParseErrorForLogging?.message ||
          'Original URL parsing did not yield a hostname.'
        }\n` +
        `2. Path validation also failed: ${pathValidationErrorInstance.message}`
    );
    return null; // Indicates both URL parsing and path validation failed
  }

  // This fallback should ideally not be reached with the current isValidUrlPath,
  // as it's expected to either return true or throw an error for invalid paths.
  // This log helps catch unexpected scenarios, e.g., if isValidUrlPath's behavior changes
  // or if it somehow doesn't throw but also doesn't return true.
  console.error(
    `Unexpected state for '${httpUrl}' after URL parsing and path validation attempts.` +
      (urlParseErrorForLogging
        ? ` Initial URL parse error: ${urlParseErrorForLogging.message}`
        : '')
  );
  return null;
}

function isSamePageOrRng(httpUrl: string, rngDomain: string): boolean {
  const validationResult = getDomainOrValidatePath(httpUrl);

  if (typeof validationResult === 'string') {
    // Helper returned a domain string
    const httpDomain = validationResult;
    const isSamePage = httpDomain === window.location.hostname;
    if (!rngDomain) {
      return isSamePage;
    }
    const isFromRNG = httpDomain === rngDomain;
    return isSamePage || isFromRNG;
  } else if (validationResult === true) {
    // Helper indicated a valid path (original logic implied this means true for isSamePageOrRng)
    return true;
  } else {
    // Helper returned null (validationResult is null), indicating failure in both parsing and path validation
    return false;
  }
}

export function storeAuthHeadersInSessionStorage(headers: HttpHeaders): void {
  headers
    .keys()
    .filter((k) => k.startsWith('x-fcg-'))
    .forEach((xItem) => {
      if (headers.get(xItem)) {
        sessionStorage.setItem(xItem, headers.get(xItem));
      }
    });
}

export function getAuthHeaders(): Record<string, string> {
  const xFcgKeys = Object.keys(sessionStorage).filter((k) =>
    k.startsWith('x-fcg-')
  );
  return xFcgKeys.reduce((headers, key) => {
    const val = sessionStorage.getItem(key);
    if (val !== null && val !== 'null' && val !== '') {
      headers[key] = val;
    }
    return headers;
  }, {});
}

const no_generic_error_routes = [
  'signin/viewer/mobile/mobileChecklogin.jsp',
  'cgibin/PartnerFCGRNGGameServlet/environmentdescription',
];

/**
 * Checks if a given string is a valid URL path.
 *
 * A valid URL path according to this function:
 * - Must be a non-empty string.
 * - Must start with a forward slash '/'.
 * - Must not contain query parameters ('?') or fragment identifiers ('#').
 * - Must not contain consecutive slashes (e.g., '//') except for the root path '/' itself.
 * - Must consist of characters valid for URL path segments (this is checked by the URL constructor).
 *   This includes alphanumerics, '-', '_', '.', '~', and percent-encoded characters.
 *   Raw spaces or other special characters that would make the URL constructor throw are invalid.
 *
 * @param path The string to validate.
 * @returns True if the string is a valid URL path, false otherwise.
 */
export function isValidUrlPath(path: unknown): path is string {
  // 1. Basic type check and ensure it's a string
  if (typeof path !== 'string') {
    throw new Error('Invalid type: path must be a string');
  }

  // 2. Must not be empty. "/" is valid, "" is not.
  if (path === '') {
    throw new Error('Invalid type: path must be a string');
  }

  // 3. Must start with a '/' for absolute paths as per typical usage.
  if (!path.startsWith('/')) {
    throw new Error('Invalid type: path must be a string');
  }

  // 6. Use URL constructor to check for invalid characters (e.g., spaces, control chars)
  //    and general malformations. It's good at validating percent-encoding too.
  try {
    // The 'http://dummybase.com' is required because the URL constructor
    // expects a full URL or a path relative to a base.
    // We are only interested in whether the path string itself is valid.
    new URL(path, 'http://dummybase.com');
    // If the constructor doesn't throw, the path string is structurally sound
    // enough to be a URL path component according to URL parsing rules.
    // This handles things like valid/invalid percent-encoding (e.g. "%2G" would throw).
    return true;
  } catch (e) {
    // Catches errors from `new URL()` if `path` contains invalid characters
    // (e.g., unencoded spaces like "/foo bar", or invalid %-sequences)
    // or is otherwise malformed from the perspective of the URL spec.
    throw new Error('Invalid type: path must be a valid URL path');
  }
}

function logStaleActivityTimestamp(): void {
  const lastActivityEpoch = sessionStorage.getItem('x-fcg-last-activity-epoch');
  if (lastActivityEpoch) {
    const timestamp = parseInt(lastActivityEpoch, 10);
    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutesAgo = currentTime - 5 * 60;

    if (timestamp < fiveMinutesAgo) {
      const ageSeconds = currentTime - timestamp;
      const minutes = Math.floor(ageSeconds / 60);
      const seconds = ageSeconds % 60;
      let ageString = '';
      if (minutes > 0) {
        ageString += `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      if (seconds > 0) {
        if (ageString) ageString += ' ';
        ageString += `${seconds} second${seconds !== 1 ? 's' : ''}`;
      }
      console.log(
        `x-fcg-last-activity-epoch is older than 5 minutes: ${timestamp} (age: ${ageSeconds} seconds, ~${ageString})`
      );
    }
  }
}
